import { useTranslations } from 'next-intl';
import { ClientErrorCodes, ErrorCodes } from '@mentimeter/quiz-types';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { ReactNode } from 'react';
import { Link } from '@mentimeter/ragnar-ui/link';
import { MailToLink } from '../loading/Loading';

const newline = () => <br />;
const NetworkRequirementsLink = (chunks: ReactNode) => (
  <Link href="/requirements">{chunks}</Link>
);

const getErrorMessage = (
  errorCode: ErrorCodes | ClientErrorCodes,
  translate: ReturnType<typeof useTranslations<'presenting.quiz'>>,
): ReactNode | undefined => {
  switch (errorCode) {
    case ErrorCodes.NO_AVAILABLE_QUIZ:
      return translate('error.no_available_quiz_error_message');
    case ClientErrorCodes.MULTIPLE_DEVICES:
      return translate('error.multiple_devices_error_message');
    case ClientErrorCodes.RESULTS_FETCH:
      return translate('error.results_fetch_error_message');
    case ClientErrorCodes.NO_CONNECTION:
      return translate.rich('error.no_connection_error_message', {
        requirementsLink: NetworkRequirementsLink,
        newline,
      });
    default:
      return undefined;
  }
};

export const Error = ({
  errorCode,
}: { errorCode: ErrorCodes | ClientErrorCodes }) => {
  const t = useTranslations('presenting.quiz');
  const errorMessage = getErrorMessage(errorCode, t);

  return (
    <Box maxWidth="600px" alignItems="center">
      {errorMessage && (
        <Text
          textAlign="center"
          color="text"
          fontSize="175"
          fontWeight="semiBold"
        >
          {errorMessage}
        </Text>
      )}
      <Text mt={3} textAlign="center" fontSize="125">
        {t.rich('error.general_error_message', {
          link: MailToLink,
        })}
      </Text>
      {!errorMessage && (
        <Text fontFamily="monospace" textAlign="center" fontSize="125">
          {t('error.error_code_message', { errorCode })}
        </Text>
      )}
    </Box>
  );
};
